import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import {
  EnrichmentLocale,
  EnrichmentPriceItem,
  LocalizedText,
  resolveLocalized,
} from "@app/shared/models/enrichment-detail.model";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import {
  ActiveLanguage,
  LanguageConfigStore,
} from "@app/shared/services/language-config.store";
import { logger } from "@core/utils/logger";
import {
  OmniarkUiModule,
  SelectOption,
  StatusType,
} from "@corp-ais/omniark-ui";
import {
  FlatEnrichmentModel,
  isFlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichmentDataService,
} from "../../../services/enrichment-data.service";
import { ActivePriceStore } from "../enrichment-edit-package/active-price.store";
import { EnrichmentPriceDirtyStore } from "../enrichment-edit-package/enrichment-price-dirty.store";
import { SectionDirtyStore } from "../enrichment-edit-package/section-dirty.store";
import {
  entriesToForm,
  ictDateToEndISO,
  ictDateToStartISO,
  isoToICTDate,
  MAX_PLA_PERIODS,
  minNewRowStartDate,
  PeriodRow,
  periodStatus,
  PeriodStatus,
  PLA_PACKAGE_TYPES,
  rowsToEntries,
  validatePeriodRows,
} from "./special-promotion.util";

type PeriodGroup = FormGroup<{
  id: FormControl<string>;
  startDate: FormControl<string>;
  endDate: FormControl<string>;
}>;

/**
 * Special promotion tab — Net X2/X3 `pricingLogicAlgorithm` editor
 * (myAIS channel only; the shell hides the step for other channels).
 */
@Component({
  selector: "app-enrichment-special-promotion",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, OmniarkUiModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-special-promotion.html",
  styleUrls: ["./enrichment-special-promotion.scss"],
})
export class EnrichmentSpecialPromotion {
  private readonly data = inject(EnrichmentDataService);
  private readonly active = inject(ActivePriceStore);
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore);
  /** Section-dirty attribution — which TAB the discard snapshot should
   *  blame. Optional: absent when the tab is mounted standalone in specs. */
  private readonly sectionDirty = inject(SectionDirtyStore, {
    optional: true,
  });
  private readonly enrichmentService = inject(EnrichmentService);
  private readonly languageConfig = inject(LanguageConfigStore);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly maxPeriods = MAX_PLA_PERIODS;

  /** Active authoring languages — one row per language in the readonly
   *  package-name breakdown below. */
  protected readonly languages = computed<ActiveLanguage[]>(() =>
    this.languageConfig.active(),
  );

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  protected readonly selectedPrice = this.active.price;

  /* ── Form state ───────────────────────────────────────────────────── */

  protected readonly packageTypeOptions: SelectOption[] = [
    { value: "", label: "— Select package type —" },
    ...PLA_PACKAGE_TYPES.map((t) => ({ value: t, label: t })),
  ];

  protected readonly form = new FormGroup({
    popId: new FormControl<string>("", { nonNullable: true }),
    packageType: new FormControl<string>("", { nonNullable: true }),
    periods: new FormArray<PeriodGroup>([]),
  });

  /** Looked-up display name of the X2/X3 POP — readonly, saved as each
   *  entry's `description`. Always the English resolution (see `onSearch`),
   *  which is the only string the wire `description` field can carry. */
  protected readonly packageName = signal<string>("");

  /** Raw locale map from the last successful POP lookup this session —
   *  feeds the per-language breakdown so Marketing can confirm the right
   *  POP resolved in every authoring language, not just English. `undefined`
   *  until a fresh Search or the hydrate-time background lookup
   *  (`refreshNameBreakdown`) resolves; while unset, the display falls back
   *  to the plain `packageName` string. */
  protected readonly packageNameLocales = signal<LocalizedText | undefined>(
    undefined,
  );

  /** One row per active language for the readonly package-name display.
   *  Empty until `packageNameLocales` is populated by a fresh Search. */
  protected readonly packageNameRows = computed<
    { locale: string; label: string; value: string }[]
  >(() => {
    const raw = this.packageNameLocales();
    if (raw == null) return [];
    return this.languages().map((lang) => ({
      locale: lang.locale,
      label: lang.shortCode || lang.label,
      value: resolveLocalized(raw, lang.locale as EnrichmentLocale, true),
    }));
  });

  protected readonly lookupState = signal<
    "idle" | "loading" | "ok" | "notfound"
  >("idle");

  /** Form value bridged to a signal so errors/status recompute on edits. */
  private readonly formValue = toSignal(this.form.valueChanges, {
    initialValue: null,
  });

  protected readonly errors = computed<string[]>(() => {
    void this.formValue();
    const rows = this.currentRows();
    const errs = validatePeriodRows(rows, this.todayICT());
    if (rows.length > 0) {
      if (!this.form.controls.popId.value.trim()) {
        errs.unshift("POP ID is required.");
      }
      const packageType = this.form.controls.packageType.value;
      if (!packageType) {
        errs.unshift("Package type is required.");
      } else if (
        !(PLA_PACKAGE_TYPES as readonly string[]).includes(packageType)
      ) {
        // Legacy/batch entries can hydrate a name outside the whitelist —
        // without this check the BE would 400 the whole save (validatePLA).
        errs.unshift("Package type must be InternetX2 or InternetX3.");
      }
      if (!this.packageName().trim()) {
        errs.unshift("Search the POP ID to resolve the package name.");
      }
    }
    return errs;
  });

  get periods(): FormArray<PeriodGroup> {
    return this.form.controls.periods;
  }

  /* ── Hydration ────────────────────────────────────────────────────────
     Re-hydrate when (a) the selected price changes or (b) the stored
     entries change underneath us — initial load and the post-save re-seed
     (which carries BE-stamped ids). Our own write-back echo is filtered
     out because writeBack() records exactly what it wrote. */

  private hydratedPriceId: string | null = null;
  private lastEntriesJson = "";

  /** POP id the current `packageName` was resolved for — by a successful
   *  Search or by hydration (the stored description belongs to the stored
   *  plaSpecId). Editing popId away from it invalidates the name so a stale
   *  "name of A, plaSpecId of B" pair can never be written back. */
  private resolvedPopId = "";

  constructor() {
    effect(() => {
      const price = this.selectedPrice();
      if (!price) return;
      const entriesJson = JSON.stringify(price.pricingLogicAlgorithm ?? []);
      if (
        this.hydratedPriceId === price.id &&
        this.lastEntriesJson === entriesJson
      ) {
        return;
      }
      this.hydratedPriceId = price.id;
      this.lastEntriesJson = entriesJson;
      this.hydrate(price);
    });

    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.invalidateStaleLookup();
      this.writeBack();
    });
  }

  /** Clear the looked-up package name when popId no longer matches the id
   *  it was resolved for. The "Search the POP ID..." error then blocks
   *  write-back until the user searches again. */
  private invalidateStaleLookup(): void {
    const popId = this.form.controls.popId.value.trim();
    if (this.packageName() && popId !== this.resolvedPopId) {
      this.packageName.set("");
      this.packageNameLocales.set(undefined);
      this.lookupState.set("idle");
    }
  }

  private hydrate(price: EnrichmentPriceItem): void {
    const state = entriesToForm(price.pricingLogicAlgorithm);
    this.form.controls.popId.setValue(state.popId, { emitEvent: false });
    this.form.controls.packageType.setValue(state.packageType, {
      emitEvent: false,
    });
    this.packageName.set(state.packageName);
    // Stored entries only carry the single (English) `description` string,
    // so clear the breakdown and rebuild it with a silent background lookup
    // (refreshNameBreakdown) — the plain string stays as fallback meanwhile.
    this.packageNameLocales.set(undefined);
    this.lookupState.set(state.packageName ? "ok" : "idle");
    this.resolvedPopId = state.popId;
    // Rebuild the FormArray wholesale — rows are tracked by $index in the
    // template, so replacing the array is safe (track-by-id + clear/push
    // would leave stale formGroupName refs; see project memory).
    this.form.setControl(
      "periods",
      new FormArray<PeriodGroup>(state.rows.map((r) => this.buildRow(r))),
      { emitEvent: false },
    );
    this.refreshNameBreakdown(state.popId);
  }

  /** Silent background lookup so the per-language breakdown survives a
   *  reload. Writes ONLY `packageNameLocales`: `packageName` (the wire
   *  `description` source), `lookupState`, and write-back stay untouched —
   *  an explicit Search remains their only writer, and a failed lookup
   *  quietly leaves the single-string fallback in place. */
  private refreshNameBreakdown(popId: string): void {
    if (!popId) return;
    this.enrichmentService
      .getEnrichmentPriceById(popId, { ...ENRICHMENT_HEADER_TRANSPORT })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Drop stale responses — the user may have edited popId (or
          // switched price, which rewrites the form) while in flight.
          if (this.form.controls.popId.value.trim() !== popId) return;
          if (res.data?.name != null) {
            this.packageNameLocales.set(res.data.name);
          }
        },
        error: (error) => {
          logger.warn(
            `[special-promotion] background name lookup failed for ${popId}:`,
            error,
          );
        },
      });
  }

  private buildRow(row?: PeriodRow): PeriodGroup {
    return new FormGroup({
      id: new FormControl<string>(row?.id ?? "", { nonNullable: true }),
      startDate: new FormControl<string>(row?.startDate ?? "", {
        nonNullable: true,
      }),
      endDate: new FormControl<string>(row?.endDate ?? "", {
        nonNullable: true,
      }),
    });
  }

  private currentRows(): PeriodRow[] {
    return this.periods.controls.map((g) => {
      const v = g.getRawValue();
      return {
        ...(v.id ? { id: v.id } : {}),
        startDate: this.toDateString(v.startDate),
        endDate: this.toDateString(v.endDate),
      };
    });
  }

  /** enr-input date controls can emit "YYYY-MM-DD", "YYYY/MM/DD" or a Date
   *  (local midnight). Normalize to the ICT calendar date string. */
  private toDateString(v: unknown): string {
    if (v instanceof Date) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, "0");
      const d = String(v.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    if (typeof v === "string") return v.replaceAll("/", "-").slice(0, 10);
    return "";
  }

  /** Today's ICT calendar date, e.g. "2026-07-05". */
  private todayICT(): string {
    return isoToICTDate(new Date().toISOString());
  }

  /* ── Row actions ─────────────────────────────────────────────────── */

  addRow(): void {
    if (this.periods.length >= MAX_PLA_PERIODS) return;
    // Appending (not clear+push) keeps existing groups' directive refs valid.
    this.periods.push(this.buildRow(), { emitEvent: false });
    this.form.updateValueAndValidity(); // single valueChanges tick
  }

  removeRow(index: number): void {
    this.periods.removeAt(index); // emits → writeBack; [] clears the config
  }

  protected statusFor(index: number): PeriodStatus | null {
    void this.formValue();
    const row = this.currentRows()[index];
    if (!row || !row.startDate || !row.endDate) return null;
    return periodStatus(
      ictDateToStartISO(row.startDate),
      ictDateToEndISO(row.endDate),
      new Date(),
    );
  }

  /** Map a period's status to an omniark `enr-status` type. */
  protected statusType(index: number): StatusType {
    switch (this.statusFor(index)) {
      case "Active":
        return "active";
      case "Expired":
        return "expired";
      case "Pending":
        return "pending";
      default:
        return "pending";
    }
  }

  /** "YYYY-MM-DD" → local-midnight Date for the picker's [minDate]. */
  private toPickerDate(date: string): Date | undefined {
    if (!date) return undefined;
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  /** [minDate] for a row's startDate picker — max(today, latest prior
   *  end + 1 day). Persisted rows return undefined (exempt, per spec). */
  protected startMinFor(index: number): Date | undefined {
    void this.formValue();
    const min = minNewRowStartDate(this.currentRows(), index, this.todayICT());
    return min ? this.toPickerDate(min) : undefined;
  }

  /** [minDate] for a row's endDate picker — its own startDate (UX only;
   *  validation already enforces end >= start). */
  protected endMinFor(index: number): Date | undefined {
    void this.formValue();
    return this.toPickerDate(this.currentRows()[index]?.startDate ?? "");
  }

  /* ── POP lookup ──────────────────────────────────────────────────── */

  onSearch(): void {
    const id = this.form.controls.popId.value.trim();
    if (!id) return;
    const forPrice = this.active.popId();
    this.lookupState.set("loading");
    this.enrichmentService
      .getEnrichmentPriceById(id, { ...ENRICHMENT_HEADER_TRANSPORT })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (this.active.popId() !== forPrice) return;
          const name = resolveLocalized(res.data?.name, "en-US");
          if (!name) {
            this.packageName.set("");
            this.packageNameLocales.set(undefined);
            this.lookupState.set("notfound");
            return;
          }
          this.packageName.set(name);
          this.packageNameLocales.set(res.data?.name);
          this.lookupState.set("ok");
          this.resolvedPopId = id;
          this.writeBack();
        },
        error: (error) => {
          logger.error(
            `[special-promotion] POP lookup failed for ${id}:`,
            error,
          );
          this.packageName.set("");
          this.packageNameLocales.set(undefined);
          this.lookupState.set("notfound");
        },
      });
  }

  /* ── Write-back ──────────────────────────────────────────────────── */

  private writeBack(): void {
    const m = this.model();
    const id = this.active.popId();
    if (!m || !id || !isFlatEnrichmentModel(m)) return;
    if (this.errors().length > 0) return; // model keeps the last-valid state
    const entries = rowsToEntries(
      this.form.controls.popId.value.trim(),
      this.form.controls.packageType.value,
      this.packageName().trim(),
      this.currentRows(),
    );
    const next = structuredClone(m) as FlatEnrichmentModel;
    const flat = next.__detail.prices?.find((p) => p.id === id);
    if (!flat) return;
    flat.pricingLogicAlgorithm = entries;
    this.lastEntriesJson = JSON.stringify(entries);
    this.data.setEnrichment(next);
    // Price-level edit: mark the owning price so `savePrices` targets it,
    // AND stamp the tab so the dirty dot/save bar blame Special promotion.
    this.priceDirty.markDirty(id);
    this.sectionDirty?.markTabDirty("special-promotion");
  }
}
